import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { useGlobal } from '../contexts/GlobalContext';
import { useDeploy } from '../contexts/DeployContext';
import { useBuild } from '../contexts/BuildContext';
import AlgoTriggerSection from './AlgoTriggerSection';

function FeedAlgoForm({ feed, onSaved, onClear, onTestStateChange }) {
  const { accountId, userId } = useGlobal();
  const { deployApi } = useDeploy();
  const { feedBuilderApi } = useBuild();

  const [code, setCode] = useState(feed?.algorithm ?? '');
  const [feedType, setFeedType] = useState(feed?.feed_type ?? 'algorithm');
  const [feedInputs, setFeedInputs] = useState(feed?.feed_inputs ?? 'user_id');
  const [sdkVersion, setSdkVersion] = useState(feed?.sdk_version ?? 'V1');
  const [name, setName] = useState(feed?.name ?? '');
  const [description, setDescription] = useState(feed?.description ?? '');
  const [imageUrl, setImageUrl] = useState(feed?.image_url ?? '');
  const [shortName, setShortName] = useState(feed?.short_name ?? '');
  const [shortDescription, setShortDescription] = useState(feed?.short_description ?? '');
  const [isPublic, setIsPublic] = useState(feed?.is_public ?? false);
  const [signature, setSignature] = useState(feed?.signature ?? '');
  const [trigger, setTrigger] = useState(feed?.trigger ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validateInProgress, setValidateInProgress] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    setCode(feed?.algorithm ?? '');
    setName(feed?.name ?? '');
    setDescription(feed?.description ?? '');
    setImageUrl(feed?.image_url ?? '');
    setShortName(feed?.short_name ?? '');
    setShortDescription(feed?.short_description ?? '');
    setIsPublic(feed?.is_public ?? false);
    setFeedType(feed?.feed_type ?? 'algorithm');
    setFeedInputs(feed?.feed_inputs ?? 'user_id');
    setSdkVersion(feed?.sdk_version ?? 'V1');
    setSignature(feed?.signature ?? '');
    setTrigger(feed?.trigger ?? null);
  }, [feed]);

  useEffect(() => {
    onTestStateChange?.({
      algorithm: code,
      sdkVersion,
      feedType,
      feedInputs,
      signature: signature?.trim() || '',
      validateInProgress,
    });
  }, [code, sdkVersion, feedType, feedInputs, signature, validateInProgress, onTestStateChange]);

  useEffect(() => {
    setValidationResult(null);
  }, [code]);

  const handleValidate = useCallback(async () => {
    if (!code.trim()) return;
    setValidateInProgress(true);
    setValidationResult(null);
    try {
      const result = await feedBuilderApi.validateFeed(code.trim(), sdkVersion, feedType, feedInputs);
      const r = result?.result ?? result;
      const syntaxOk = Boolean(r?.syntax_check);
      const securityOk = Boolean(r?.security_check);
      const sig = syntaxOk && securityOk ? r?.signature : undefined;
      if (sig) setSignature(sig);
      if (syntaxOk && securityOk) {
        setValidationResult({ success: true, message: 'Validation passed' });
      } else {
        const parts = [];
        if (!syntaxOk) parts.push(r?.syntax_message ?? 'Syntax check failed');
        if (!securityOk) parts.push(r?.security_message ?? 'Security check failed');
        setValidationResult({ success: false, message: parts.join('. ') });
      }
    } catch (err) {
      setValidationResult({ success: false, message: err?.message ?? 'Validation failed' });
    } finally {
      setValidateInProgress(false);
    }
  }, [feedBuilderApi, code, sdkVersion, feedType, feedInputs]);

  const handleSave = useCallback(async () => {
    const trimmedCode = code.trim();
    const sig = signature?.trim();

    if (!trimmedCode) {
      setSaveError('Algorithm is required');
      return;
    }
    if (!sig) {
      setSaveError('Validate the algorithm or paste a signature before saving');
      return;
    }
    if (!name?.trim()) {
      setSaveError('Name is required');
      return;
    }

    setSaveError(null);
    setSaveSuccess(false);
    setIsSaving(true);
    try {
      const data = {
        name: name.trim() || undefined,
        description: description.trim() || undefined,
        image_url: imageUrl.trim() || undefined,
        short_name: shortName.trim() || undefined,
        short_description: shortDescription.trim() || undefined,
        feed_type: feedType,
        feed_inputs: feedInputs,
        sdk_version: sdkVersion,
        algorithm: trimmedCode,
        signature: sig,
        is_public: isPublic,
        trigger: trigger ?? undefined,
      };

      if (feed?.algo_id) {
        await deployApi.updateFeedAlgo(feed.algo_id, data);
        setSaveSuccess(true);
        onSaved?.();
      } else {
        await deployApi.createFeedAlgo({
          ...data,
          account_id: accountId,
          user_id: userId,
        });
        setSaveSuccess(true);
        onSaved?.();
        onClear?.();
      }
    } catch (err) {
      setSaveError(err?.message ?? 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [feed, name, description, imageUrl, shortName, shortDescription, isPublic, accountId, userId, code, feedType, feedInputs, sdkVersion, signature, trigger, deployApi, onSaved, onClear]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <FormControlLabel
            control={<Checkbox checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />}
            label="Public"
          />
          {!feed?.algo_id && (
            <>
              <TextField
                size="small"
                label="Account ID"
                value={accountId ?? ''}
                placeholder="Account ID"
                required
                disabled
              />
              <TextField
                size="small"
                label="User ID"
                value={userId ?? ''}
                placeholder="MBD User ID"
                disabled
              />
            </>
          )}
          <TextField
            size="small"
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Feed algo name"
            fullWidth
          />
          <TextField
            size="small"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Feed algo description"
            fullWidth
            multiline
          />
          <TextField
            size="small"
            label="Short name"
            value={shortName}
            onChange={(e) => setShortName(e.target.value)}
            placeholder="Short name"
          />
          <TextField
            size="small"
            label="Short description"
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            placeholder="Short description"
            fullWidth
          />
          <TextField
            size="small"
            label="Image URL"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://..."
            fullWidth
          />
          <TextField
            size="small"
            select
            label="Feed type"
            value={feedType}
            onChange={(e) => setFeedType(e.target.value)}
          >
            <MenuItem value="algorithm">algorithm</MenuItem>
            <MenuItem value="minifeed">minifeed</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="Feed inputs"
            value={feedInputs}
            onChange={(e) => setFeedInputs(e.target.value)}
          >
            <MenuItem value="none">none</MenuItem>
            <MenuItem value="user_id">user_id</MenuItem>
          </TextField>
          <TextField
            size="small"
            select
            label="DSL version"
            value={sdkVersion}
            onChange={(e) => setSdkVersion(e.target.value)}
          >
            <MenuItem value="V1">V1</MenuItem>
          </TextField>
        </Box>
        <TextField
          size="small"
          multiline
          minRows={10}
          maxRows={24}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="Algorithm code..."
          fullWidth
          sx={{
            mt: 1.5,
            '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 },
          }}
        />
        <TextField
          size="small"
          label="Signature"
          value={signature}
          onChange={(e) => setSignature(e.target.value)}
          placeholder="Validate the feed algo or paste a signature"
          fullWidth
          sx={{ mt: 1.5 }}
        />
        <AlgoTriggerSection
          algorithm={code}
          trigger={trigger}
          onChange={setTrigger}
        />
        {(validationResult || saveError || saveSuccess) && (
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {validationResult && (
              <Alert severity={validationResult.success ? 'success' : 'error'}>
                {validationResult.message}
              </Alert>
            )}
            {saveSuccess && (
              <Alert severity="success">Feed algo saved successfully</Alert>
            )}
            {saveError && (
              <Alert severity="error">{saveError}</Alert>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
          {code?.trim() && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleValidate}
              disabled={validateInProgress}
              startIcon={validateInProgress ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {validateInProgress ? 'Validating...' : 'Validate'}
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isSaving || !signature?.trim()}
          >
            Save feed algo
          </Button>
          {isSaving && <CircularProgress size={24} />}
        </Box>
      </Box>
  );
}

export default FeedAlgoForm;
